import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { User, UserRole } from '@meshalert/contract';
import { ApiError, api } from './api.ts';

type SessionValue = {
  user: User | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (...roles: UserRole[]) => boolean;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.session.current();
      } catch (error) {
        // Not being logged in is a normal state, not a failure to retry.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const signInMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => api.session.create(input),
    onSuccess: (result) => queryClient.setQueryData(['session'], result.user),
  });

  const signOutMutation = useMutation({
    mutationFn: () => api.session.destroy(),
    onSuccess: () => {
      queryClient.setQueryData(['session'], null);
      queryClient.clear();
    },
  });

  const user = data ?? null;

  const value: SessionValue = {
    user,
    isLoading,
    signIn: async (email, password) => {
      await signInMutation.mutateAsync({ email, password });
    },
    signOut: async () => {
      await signOutMutation.mutateAsync();
    },
    can: (...roles) => (user ? roles.includes(user.role) : false),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession ausserhalb von SessionProvider');
  return value;
}
